import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { MessengerCallOverlay } from '../components/messaging/MessengerCallOverlay';
import { CallPipBubble } from '../components/messaging/CallPipBubble';
import { IncomingCallHeadsUp } from '../components/messaging/IncomingCallHeadsUp';
import { startRingtone, startRingback, stopAllCallAudio, stopRingtone } from '../utils/callAudio';
import { switchRoleIfNeeded } from '../utils/roleSwitch';
import {
  addNativeAppStateListener,
  addNativeBackButtonListener,
  addNativeCallActionListener,
  clearPendingCallAction,
  isCallNativeAvailable,
  isNativeAndroid,
  nativeClearIncoming,
  nativeClearOngoingCall,
  nativeEndCallSession,
  nativeGetPendingCallAction,
  nativeMinimizeApp,
  nativeSetSpeaker,
  nativeShowOngoingCall,
  nativeStartCallSession,
  readPendingCallAction,
  writePendingCallAction,
} from '../utils/callNative';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SOCKET_PATH = '/api/socket.io';
const VOICE_CALL_ALLOWED_ROLES = new Set(['guest', 'host']);
const RING_TIMEOUT_MS = 45000;
const CONNECT_TIMEOUT_MS = 40000;
const RECONNECT_WINDOW_MS = 20000;
const CALL_SYNC_INTERVAL_MS = 4000;
const SIGNAL_POLL_MS = 700;
const chatRouteFor = (role, conversationId) => `/${role === 'host' ? 'host' : 'guest'}/chat/${conversationId}`;
const isAllowedRole = (user) => Boolean(user && VOICE_CALL_ALLOWED_ROLES.has(user.role));

const CallContext = createContext(null);

export function CallProvider({ children, user }) {
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const signalPollRef = useRef(null);
  const reconnectIntervalRef = useRef(null);
  const reconnectDeadlineRef = useRef(null);
  const currentCallRef = useRef(null);
  const lastSignalAtRef = useRef(null);
  const pendingCandidatesRef = useRef([]);
  const makingOfferRef = useRef(false);
  const initiatingRef = useRef(false);
  const acceptingRef = useRef(false);
  const pendingAcceptRef = useRef(null);
  const lastActionKeyRef = useRef(null);
  const audioSessionCallIdRef = useRef(null);
  const ongoingNotifiedCallIdRef = useRef(null);
  const userRef = useRef(user);
  const minimizedRef = useRef(false);
  const overlayStatusRef = useRef('idle');
  const isReconnectingRef = useRef(false);
  const handleCallActionRef = useRef(() => {});

  const [currentCall, setCurrentCall] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [speakerOn, setSpeakerOn] = useState(() => !isCallNativeAvailable());
  const [socketConnected, setSocketConnected] = useState(false);
  const [callStartedAtMs, setCallStartedAtMs] = useState(null);
  const [callDurationSeconds, setCallDurationSeconds] = useState(0);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [minimized, setMinimized] = useState(false);

  useEffect(() => { currentCallRef.current = currentCall; }, [currentCall]);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { minimizedRef.current = minimized; }, [minimized]);
  useEffect(() => { isReconnectingRef.current = isReconnecting; }, [isReconnecting]);

  const getAuthHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const fetchIceServers = useCallback(async (callId) => {
    const response = await axios.get(`${API}/calls/${callId}/ice-servers`, { headers: getAuthHeaders() });
    return response.data?.ice_servers || [];
  }, [getAuthHeaders]);

  const endCallOnServer = useCallback((callId, reason) => {
    if (!callId) return Promise.resolve();
    return axios.post(`${API}/calls/${callId}/end`, { reason }, { headers: getAuthHeaders() }).catch(() => {});
  }, [getAuthHeaders]);

  const cleanupCallMedia = useCallback(() => {
    if (signalPollRef.current) {
      window.clearInterval(signalPollRef.current);
      signalPollRef.current = null;
    }
    lastSignalAtRef.current = null;
    pendingCandidatesRef.current = [];
    makingOfferRef.current = false;
    if (reconnectIntervalRef.current) {
      window.clearInterval(reconnectIntervalRef.current);
      reconnectIntervalRef.current = null;
    }
    reconnectDeadlineRef.current = null;
    peerConnectionRef.current?.close();
    peerConnectionRef.current = null;
    localStreamRef.current?.getTracks()?.forEach((track) => track.stop());
    localStreamRef.current = null;
    remoteStreamRef.current?.getTracks?.()?.forEach((track) => track.stop());
    remoteStreamRef.current = null;
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
  }, []);

  const finishCall = useCallback((reason = '') => {
    const endedCall = currentCallRef.current;
    cleanupCallMedia();
    stopAllCallAudio();
    nativeEndCallSession();
    nativeClearOngoingCall();
    if (endedCall?.id) nativeClearIncoming(endedCall.id);
    clearPendingCallAction();
    pendingAcceptRef.current = null;
    audioSessionCallIdRef.current = null;
    ongoingNotifiedCallIdRef.current = null;
    setCurrentCall(null);
    setIsMuted(false);
    setSpeakerOn(!isCallNativeAvailable());
    setCallStartedAtMs(null);
    setCallDurationSeconds(0);
    setIsReconnecting(false);
    setMinimized(false);
    if (reason === 'declined') toast.message('Call declined');
    if (reason === 'missed' || reason === 'no_answer') toast.message(endedCall?.direction === 'incoming' ? 'Missed voice call' : 'No answer');
    if (reason === 'connection_lost' || reason === 'connection_failed') toast.error("The call couldn't stay connected.");
    if (reason === 'answered_elsewhere') toast.message('Call answered on another device');
  }, [cleanupCallMedia]);

  useEffect(() => {
    if (!callStartedAtMs) return undefined;
    const intervalId = window.setInterval(() => {
      setCallDurationSeconds(Math.max(0, Math.floor((Date.now() - callStartedAtMs) / 1000)));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [callStartedAtMs]);

  const requestNotificationPermission = useCallback(async () => {
    const Notif = typeof window !== 'undefined' ? window.Notification : undefined;
    if (!Notif) return 'unsupported';
    if (Notif.permission !== 'default') return Notif.permission;
    try {
      return await Notif.requestPermission();
    } catch {
      return 'default';
    }
  }, []);

  const showIncomingCallNotification = useCallback(async (call) => {
    if (isNativeAndroid()) return;
    const Notif = typeof window !== 'undefined' ? window.Notification : undefined;
    if (!Notif || Notif.permission !== 'granted') return;
    const title = `Incoming call from ${call?.other_user?.name || 'Allure contact'}`;
    const options = {
      body: 'Open Allure to answer the call.',
      tag: `allure-call-${call?.id}`,
      icon: '/allure-logo.png',
      badge: '/allure-logo.png',
      requireInteraction: true,
      data: { url: `${window.location.origin}${call?.conversation_id ? chatRouteFor(call.my_role || userRef.current?.role, call.conversation_id) : '/'}` },
    };
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        await registration.showNotification(title, options);
      } else {
        new Notif(title, options);
      }
    } catch { /* noop */ }
  }, []);

  const postSignal = useCallback(async (callId, targetUserId, signalType, payload) => {
    await axios.post(`${API}/calls/${callId}/signals`, {
      target_user_id: targetUserId,
      signal_type: signalType,
      payload,
    }, { headers: getAuthHeaders() });
  }, [getAuthHeaders]);

  const flushPendingCandidates = useCallback(async (peerConnection) => {
    while (pendingCandidatesRef.current.length) {
      const candidate = pendingCandidatesRef.current.shift();
      try { await peerConnection.addIceCandidate(new RTCIceCandidate(candidate)); } catch { /* stale candidate */ }
    }
  }, []);

  // "Perfect negotiation" (lite): the callee is the polite peer. Re-offers
  // (ICE restarts, rejoin after a reload) are applied whenever we're stable;
  // on a collision the polite side rolls back, the impolite side ignores.
  const applySignal = useCallback(async (signal) => {
    const call = currentCallRef.current;
    const peerConnection = peerConnectionRef.current;
    if (!call || !peerConnection || call.id !== signal.call_id) return;

    if (signal.signal_type === 'offer') {
      const polite = call.direction === 'incoming';
      const collision = makingOfferRef.current || peerConnection.signalingState !== 'stable';
      if (collision && !polite) return;
      if (collision) {
        await peerConnection.setLocalDescription({ type: 'rollback' });
      }
      await peerConnection.setRemoteDescription(new RTCSessionDescription(signal.payload));
      await flushPendingCandidates(peerConnection);
      const answer = await peerConnection.createAnswer();
      await peerConnection.setLocalDescription(answer);
      await postSignal(call.id, call.other_user.id, 'answer', answer);
      return;
    }

    if (signal.signal_type === 'answer') {
      if (peerConnection.signalingState === 'have-local-offer') {
        await peerConnection.setRemoteDescription(new RTCSessionDescription(signal.payload));
        await flushPendingCandidates(peerConnection);
      }
      return;
    }

    if (signal.signal_type === 'ice-candidate') {
      if (peerConnection.remoteDescription) {
        try { await peerConnection.addIceCandidate(new RTCIceCandidate(signal.payload)); } catch { /* stale candidate */ }
      } else {
        pendingCandidatesRef.current.push(signal.payload);
      }
    }
  }, [flushPendingCandidates, postSignal]);

  const pollSignals = useCallback(async (callId) => {
    try {
      const response = await axios.get(`${API}/calls/${callId}/signals`, {
        params: lastSignalAtRef.current ? { after: lastSignalAtRef.current } : {},
        headers: getAuthHeaders(),
      });
      for (const signal of response.data || []) {
        lastSignalAtRef.current = signal.created_at || lastSignalAtRef.current;
        try { await applySignal(signal); } catch { /* keep consuming */ }
      }
    } catch (error) {
      const status = error?.response?.status;
      if ((status === 410 || status === 404) && currentCallRef.current?.id === callId) {
        finishCall('hangup');
      }
    }
  }, [applySignal, finishCall, getAuthHeaders]);

  const startSignalPolling = useCallback((callId) => {
    if (signalPollRef.current) window.clearInterval(signalPollRef.current);
    signalPollRef.current = window.setInterval(() => { pollSignals(callId); }, SIGNAL_POLL_MS);
  }, [pollSignals]);

  const attemptReconnect = useCallback(async () => {
    const call = currentCallRef.current;
    const peerConnection = peerConnectionRef.current;
    if (!call || !peerConnection || !call.other_user?.id) return;
    if (!reconnectDeadlineRef.current) reconnectDeadlineRef.current = Date.now() + RECONNECT_WINDOW_MS;
    if (Date.now() > reconnectDeadlineRef.current) {
      endCallOnServer(call.id, 'connection_lost');
      finishCall('connection_lost');
      return;
    }
    if (peerConnection.signalingState !== 'stable' || makingOfferRef.current) return;
    try {
      setIsReconnecting(true);
      makingOfferRef.current = true;
      peerConnection.restartIce?.();
      const offer = await peerConnection.createOffer({ iceRestart: true });
      await peerConnection.setLocalDescription(offer);
      await postSignal(call.id, call.other_user.id, 'offer', offer);
    } catch { /* retried by the reconnect interval */ } finally {
      makingOfferRef.current = false;
    }
  }, [endCallOnServer, finishCall, postSignal]);

  const createPeerConnection = useCallback((call, iceServers) => {
    cleanupCallMedia();
    const peerConnection = new RTCPeerConnection({ iceServers: iceServers || [] });
    peerConnectionRef.current = peerConnection;
    remoteStreamRef.current = new MediaStream();
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = remoteStreamRef.current;
      remoteAudioRef.current.autoplay = true;
      remoteAudioRef.current.playsInline = true;
    }

    peerConnection.ontrack = (event) => {
      const tracks = event.streams?.[0] ? event.streams[0].getTracks() : [event.track];
      tracks.forEach((track) => remoteStreamRef.current?.addTrack(track));
      remoteAudioRef.current?.play?.().catch(() => {});
    };

    const handleStateChange = (state) => {
      if (state === 'connected' || state === 'completed') {
        setIsReconnecting(false);
        reconnectDeadlineRef.current = null;
        return;
      }
      if ((state === 'disconnected' || state === 'failed') && currentCallRef.current?.status !== 'ended') {
        attemptReconnect();
      }
    };
    peerConnection.onconnectionstatechange = () => handleStateChange(peerConnection.connectionState);
    peerConnection.oniceconnectionstatechange = () => handleStateChange(peerConnection.iceConnectionState);

    const targetUserId = call.other_user?.id;
    peerConnection.onicecandidate = async (event) => {
      if (!event.candidate || !targetUserId) return;
      try {
        await postSignal(call.id, targetUserId, 'ice-candidate', event.candidate.toJSON());
      } catch { /* noop */ }
    };

    return peerConnection;
  }, [attemptReconnect, cleanupCallMedia, postSignal]);

  const ensureLocalStream = useCallback(async () => {
    if (localStreamRef.current) return localStreamRef.current;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    localStreamRef.current = stream;
    return stream;
  }, []);

  const attachLocalTracks = useCallback(async (peerConnection) => {
    const stream = await ensureLocalStream();
    stream.getAudioTracks().forEach((track) => {
      peerConnection.addTrack(track, stream);
      track.enabled = !isMuted;
    });
  }, [ensureLocalStream, isMuted]);

  // Re-establish media for a call that is still live on the server but has
  // no peer connection on this device (WebView reload / process restart).
  const rejoinCall = useCallback(async (call) => {
    if (!call?.other_user?.id || peerConnectionRef.current) return;
    try {
      const iceServers = await fetchIceServers(call.id);
      const peerConnection = createPeerConnection(call, iceServers);
      await attachLocalTracks(peerConnection);
      // Skip every signal from the previous session — only fresh ones matter.
      const existing = await axios.get(`${API}/calls/${call.id}/signals`, { headers: getAuthHeaders() });
      const last = (existing.data || []).slice(-1)[0];
      lastSignalAtRef.current = last?.created_at || null;
      startSignalPolling(call.id);
      makingOfferRef.current = true;
      const offer = await peerConnection.createOffer({ iceRestart: true });
      await peerConnection.setLocalDescription(offer);
      await postSignal(call.id, call.other_user.id, 'offer', offer);
    } catch { /* the sync loop settles it */ } finally {
      makingOfferRef.current = false;
    }
  }, [attachLocalTracks, createPeerConnection, fetchIceServers, getAuthHeaders, postSignal, startSignalPolling]);

  // Authoritative state sync — both phones follow the server even if a
  // socket event was lost while the WebView was backgrounded.
  const syncCallWithServer = useCallback(async () => {
    if (!isAllowedRole(userRef.current) || initiatingRef.current || acceptingRef.current) return;
    const current = currentCallRef.current;
    try {
      if (current?.id) {
        const { data } = await axios.get(`${API}/calls/${current.id}`, { headers: getAuthHeaders() });
        if (!data || data.status === 'ended') {
          finishCall(data?.ended_reason || 'hangup');
          return;
        }
        setCurrentCall((previous) => (previous && previous.id === data.id
          ? { ...previous, ...data, other_user: data.other_user || previous.other_user }
          : previous));
        if (data.started_at) setCallStartedAtMs(new Date(data.started_at).getTime());
        return;
      }
      const { data } = await axios.get(`${API}/calls/active`, { headers: getAuthHeaders() });
      if (!data || currentCallRef.current) return;
      if (data.status === 'ringing' && data.direction === 'outgoing') {
        // An outgoing ring with no local media (reload mid-dial) can't be completed.
        endCallOnServer(data.id, 'no_answer');
        return;
      }
      setCurrentCall(data);
      if (data.started_at) setCallStartedAtMs(new Date(data.started_at).getTime());
      if (data.status === 'connecting' || data.status === 'active') rejoinCall(data);
    } catch (error) {
      const status = error?.response?.status;
      if (current && (status === 404 || status === 410)) finishCall('hangup');
    }
  }, [endCallOnServer, finishCall, getAuthHeaders, rejoinCall]);

  const startVoiceCall = useCallback(async ({ conversationId, contact }) => {
    if (!isAllowedRole(userRef.current)) {
      toast.error('Voice calling is available only for guests and hosts.');
      return;
    }
    if (currentCallRef.current) {
      setMinimized(false);
      return;
    }
    let initiatedCall = null;
    initiatingRef.current = true;
    try {
      await requestNotificationPermission();
      // Grab the mic BEFORE ringing the other side so a denied permission
      // never produces a phantom missed call on their end.
      await ensureLocalStream();
      const response = await axios.post(`${API}/calls/initiate`, { conversation_id: conversationId }, { headers: getAuthHeaders() });
      const call = { ...response.data, other_user: response.data.other_user || contact || null };
      initiatedCall = call;
      if (call.status !== 'ringing' || call.direction !== 'outgoing') {
        setCurrentCall(call);
        setMinimized(false);
        if (call.status === 'connecting' || call.status === 'active') rejoinCall(call);
        return;
      }
      const iceServers = await fetchIceServers(call.id);
      const peerConnection = createPeerConnection(call, iceServers);
      await attachLocalTracks(peerConnection);
      setCurrentCall(call);
      setMinimized(false);
      startSignalPolling(call.id);
      makingOfferRef.current = true;
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);
      makingOfferRef.current = false;
      await postSignal(call.id, call.other_user.id, 'offer', offer);
    } catch (error) {
      makingOfferRef.current = false;
      if (initiatedCall?.id) endCallOnServer(initiatedCall.id, 'no_answer');
      finishCall();
      const detail = error.response?.data?.detail
        || (error?.name === 'NotAllowedError' ? 'Microphone permission is required to place calls.' : null)
        || error?.message
        || 'Unable to start the call.';
      toast.error(detail);
    } finally {
      initiatingRef.current = false;
    }
  }, [attachLocalTracks, createPeerConnection, endCallOnServer, ensureLocalStream, fetchIceServers, finishCall, getAuthHeaders, postSignal, rejoinCall, requestNotificationPermission, startSignalPolling]);

  const acceptCall = useCallback(async () => {
    const call = currentCallRef.current;
    if (!call || acceptingRef.current) return;
    acceptingRef.current = true;
    nativeClearIncoming(call.id);
    stopRingtone();
    try {
      const iceServers = await fetchIceServers(call.id);
      const peerConnection = createPeerConnection(call, iceServers);
      await attachLocalTracks(peerConnection);
      const { data } = await axios.post(`${API}/calls/${call.id}/accept`, {}, { headers: getAuthHeaders() });
      setCurrentCall((previous) => (previous
        ? { ...previous, ...(data || {}), status: data?.status || 'connecting', other_user: previous.other_user || data?.other_user }
        : previous));
      setMinimized(false);
      startSignalPolling(call.id);
      await pollSignals(call.id);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 410) {
        finishCall('missed');
        toast.error('This call has already ended.');
      } else {
        endCallOnServer(call.id, 'missed');
        finishCall('missed');
        toast.error(error.response?.data?.detail
          || (error?.name === 'NotAllowedError' ? 'Microphone permission is required to answer calls.' : null)
          || 'Unable to answer the call.');
      }
    } finally {
      acceptingRef.current = false;
    }
  }, [attachLocalTracks, createPeerConnection, endCallOnServer, fetchIceServers, finishCall, getAuthHeaders, pollSignals, startSignalPolling]);

  const declineCall = useCallback(async () => {
    const call = currentCallRef.current;
    if (!call) return;
    nativeClearIncoming(call.id);
    try {
      await axios.post(`${API}/calls/${call.id}/decline`, {}, { headers: getAuthHeaders() });
    } catch { /* noop */ } finally {
      finishCall('declined');
    }
  }, [finishCall, getAuthHeaders]);

  const endCall = useCallback(async (reason = 'hangup') => {
    const call = currentCallRef.current;
    if (!call) return;
    try {
      await axios.post(`${API}/calls/${call.id}/end`, { reason }, { headers: getAuthHeaders() });
    } catch { /* noop */ } finally {
      finishCall(reason);
    }
  }, [finishCall, getAuthHeaders]);

  const toggleMute = useCallback(() => {
    setIsMuted((current) => {
      const next = !current;
      localStreamRef.current?.getAudioTracks()?.forEach((track) => { track.enabled = !next; });
      return next;
    });
  }, []);

  const toggleSpeaker = useCallback(() => {
    const next = !speakerOn;
    setSpeakerOn(next);
    nativeSetSpeaker(next);
  }, [speakerOn]);

  const minimizeCall = useCallback(() => setMinimized(true), []);
  const expandCall = useCallback(() => setMinimized(false), []);

  // Lock-screen / notification actions delivered by the native shell
  // (accept, show, open_chat, hangup, incoming). Switches role first when
  // the call belongs to a different profile than the one being browsed.
  const handleCallAction = useCallback(async (action) => {
    if (!action?.action) return;
    // Native actions carry the device timestamp; anything older than two
    // minutes (drained long after the call) is noise.
    if (action.ts && Date.now() - Number(action.ts) > 120000) return;
    const key = `${action.action}:${action.call_id || ''}:${action.ts || ''}`;
    if (key === lastActionKeyRef.current) return;
    lastActionKeyRef.current = key;

    const kind = action.action;
    if (kind === 'hangup') {
      if (currentCallRef.current) endCall('hangup');
      return;
    }
    if (kind === 'toggle_mute') {
      if (currentCallRef.current) toggleMute();
      return;
    }
    if (kind === 'speaker_on' || kind === 'speaker_off') {
      if (currentCallRef.current) {
        const desired = kind === 'speaker_on';
        setSpeakerOn(desired);
      }
      return;
    }
    if (kind === 'initiate_callback') {
      // Native "Call back" action — start a new outgoing call for the
      // same conversation. Keeps the WebView in the background: the
      // in-app overlay will stay minimized while the native call UI
      // handles the user-facing part.
      if (!action.conversation_id) return;
      const activeUserCb = userRef.current;
      const targetRoleCb = action.role === 'host' ? 'host' : 'guest';
      if (activeUserCb && activeUserCb.role !== targetRoleCb) {
        writePendingCallAction(action);
        await switchRoleIfNeeded(targetRoleCb);
        window.location.href = `/${targetRoleCb}/chat/${action.conversation_id}`;
        return;
      }
      startVoiceCall({ conversationId: action.conversation_id, contact: null });
      setMinimized(true); // keep in-app UI hidden; native screen handles it
      return;
    }
    const activeUser = userRef.current;
    if (!activeUser) {
      writePendingCallAction(action);
      return;
    }
    const targetRole = action.role === 'host' ? 'host' : 'guest';
    const conversationId = action.conversation_id || '';
    if (activeUser.role !== targetRole) {
      writePendingCallAction(action);
      await switchRoleIfNeeded(targetRole);
      window.location.href = conversationId ? chatRouteFor(targetRole, conversationId) : '/';
      return;
    }
    if (conversationId && kind !== 'incoming') {
      const target = chatRouteFor(targetRole, conversationId);
      if (window.location.pathname !== target) navigate(target);
    }
    if (kind === 'accept') {
      pendingAcceptRef.current = action.call_id || null;
      if (currentCallRef.current?.id === action.call_id && currentCallRef.current.status === 'ringing') {
        acceptCall();
        return;
      }
    }
    if (kind === 'show') setMinimized(false);
    syncCallWithServer();
  }, [acceptCall, endCall, navigate, syncCallWithServer]);
  useEffect(() => { handleCallActionRef.current = handleCallAction; }, [handleCallAction]);

  // Auto-answer once the ringing call the user tapped "Answer" for is on screen.
  useEffect(() => {
    if (!currentCall || currentCall.status !== 'ringing' || currentCall.direction !== 'incoming') return;
    let targetId = pendingAcceptRef.current;
    if (!targetId) {
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('call_action') === 'accept') {
          targetId = params.get('call_id');
          params.delete('call_action');
          params.delete('call_id');
          window.history.replaceState({}, '', window.location.pathname + (params.toString() ? `?${params.toString()}` : ''));
        }
      } catch { /* noop */ }
    }
    if (targetId && targetId === currentCall.id) {
      pendingAcceptRef.current = null;
      acceptCall();
    }
  }, [currentCall, acceptCall]);

  // Native bridge: pending actions on launch/resume + live events.
  useEffect(() => {
    const pump = async () => {
      const pending = await nativeGetPendingCallAction();
      if (pending) handleCallActionRef.current(pending);
    };
    pump();
    const removeAction = addNativeCallActionListener((data) => handleCallActionRef.current(data));
    const removeState = addNativeAppStateListener((isActive) => { if (isActive) { pump(); syncCallWithServer(); } });
    const onVisible = () => { if (document.visibilityState === 'visible') syncCallWithServer(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      removeAction();
      removeState();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [syncCallWithServer]);

  // Actions parked across a role-switch reload (or before the user loaded).
  useEffect(() => {
    if (!user) return;
    const stored = readPendingCallAction();
    if (!stored) return;
    clearPendingCallAction();
    handleCallActionRef.current(stored);
  }, [user]);

  // Android hardware back: minimize the call screen instead of leaving it.
  useEffect(() => addNativeBackButtonListener(() => {
    if (currentCallRef.current && !minimizedRef.current && overlayStatusRef.current !== 'incoming') {
      setMinimized(true);
      return;
    }
    const idx = window.history.state?.idx;
    if (typeof idx === 'number' && idx <= 0) {
      nativeMinimizeApp();
      return;
    }
    window.history.back();
  }), []);

  useEffect(() => {
    if (!isAllowedRole(user)) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      if (currentCallRef.current) finishCall();
      return undefined;
    }
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    const socket = io(BACKEND_URL, {
      path: SOCKET_PATH,
      transports: ['websocket', 'polling'],
      auth: { token },
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setSocketConnected(true);
      if (currentCallRef.current && isReconnectingRef.current) attemptReconnect();
      syncCallWithServer();
    });
    socket.on('disconnect', () => {
      setSocketConnected(false);
      if (currentCallRef.current && ['active', 'connecting'].includes(currentCallRef.current.status)) {
        setIsReconnecting(true);
      }
    });
    socket.on('call_event', (event) => {
      const call = event?.call;
      if (!call) return;

      if (event.type === 'incoming_call') {
        if (currentCallRef.current && currentCallRef.current.id !== call.id) return;
        setCurrentCall(call);
        setMinimized(false);
        if (document.hidden) showIncomingCallNotification(call);
        return;
      }

      if (event.type === 'call_updated') {
        if (call.status === 'ended') {
          if (currentCallRef.current?.id === call.id) finishCall(call.ended_reason || 'hangup');
          return;
        }
        setCurrentCall((previous) => (previous && previous.id === call.id
          ? { ...previous, ...call, other_user: call.other_user || previous.other_user }
          : previous || call));
        if (call.started_at) setCallStartedAtMs(new Date(call.started_at).getTime());
        if (call.status === 'active') {
          setIsReconnecting(false);
          reconnectDeadlineRef.current = null;
        }
        return;
      }

      if (event.type === 'call_ended' && currentCallRef.current?.id === call.id) {
        finishCall(event.reason || call.ended_reason || 'hangup');
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setSocketConnected(false);
    };
  }, [attemptReconnect, finishCall, showIncomingCallNotification, syncCallWithServer, user?.id, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAllowedRole(user)) return undefined;
    const intervalId = window.setInterval(() => { syncCallWithServer(); }, CALL_SYNC_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [syncCallWithServer, user]);

  useEffect(() => {
    if (!isReconnecting || reconnectIntervalRef.current || !currentCall) return undefined;
    reconnectIntervalRef.current = window.setInterval(() => { attemptReconnect(); }, 4000);
    return () => {
      if (reconnectIntervalRef.current) {
        window.clearInterval(reconnectIntervalRef.current);
        reconnectIntervalRef.current = null;
      }
    };
  }, [attemptReconnect, currentCall, isReconnecting]);

  const overlayStatus = useMemo(() => {
    if (!currentCall) return 'idle';
    if (isReconnecting) return 'reconnecting';
    if (currentCall.status === 'ringing' && currentCall.direction === 'incoming') return 'incoming';
    if (currentCall.status === 'ringing') return 'calling';
    if (currentCall.status === 'active') return 'active';
    return 'connecting';
  }, [currentCall, isReconnecting]);
  useEffect(() => { overlayStatusRef.current = overlayStatus; }, [overlayStatus]);

  // Ringtone (callee) / ringback (caller). On Android native rings while
  // backgrounded, so in-app only plays when WebView is visible.
  useEffect(() => {
    if (overlayStatus === 'incoming') {
      const ringInApp = () => (!isNativeAndroid() || document.visibilityState === 'visible' ? startRingtone() : stopRingtone());
      ringInApp();
      document.addEventListener('visibilitychange', ringInApp);
      return () => document.removeEventListener('visibilitychange', ringInApp);
    }
    if (overlayStatus === 'calling') {
      startRingback();
      return undefined;
    }
    stopAllCallAudio();
    return undefined;
  }, [overlayStatus]);

  // Native audio session (earpiece by default) + ongoing-call foreground
  // service. Fired from "calling" (Android 12+ refuses from background);
  // A1: outgoing-ringing also fires it so ringback uses the earpiece.
  useEffect(() => {
    if (!currentCall) return;
    const outRinging = currentCall.status === 'ringing' && currentCall.direction === 'outgoing';
    if ((outRinging || currentCall.status === 'connecting' || currentCall.status === 'active') && audioSessionCallIdRef.current !== currentCall.id) {
      audioSessionCallIdRef.current = currentCall.id;
      const native = isCallNativeAvailable();
      setSpeakerOn(!native);
      if (native) nativeStartCallSession(false);
    }
    const key = `${currentCall.id}:${overlayStatus}`;
    if (['calling', 'connecting', 'active'].includes(overlayStatus) && ongoingNotifiedCallIdRef.current !== key) {
      ongoingNotifiedCallIdRef.current = key;
      nativeShowOngoingCall({
        callId: currentCall.id,
        name: currentCall.other_user?.name || 'Allure contact',
        conversationId: currentCall.conversation_id,
        role: currentCall.my_role || userRef.current?.role || 'guest',
        state: overlayStatus,
        startedAt: overlayStatus === 'active' ? (callStartedAtMs || Date.now()) : Date.now(),
        endUrl: `${API}/calls/${currentCall.id}/end`,
      });
    }
  }, [currentCall, overlayStatus, callStartedAtMs]);

  // Client-side safety timeouts (the server has matching watchdogs).
  useEffect(() => {
    if (!currentCall) return undefined;
    let handle = null;
    if (overlayStatus === 'calling') {
      handle = window.setTimeout(() => endCall('no_answer'), RING_TIMEOUT_MS);
    } else if (overlayStatus === 'incoming') {
      handle = window.setTimeout(() => finishCall('missed'), RING_TIMEOUT_MS + 5000);
    } else if (overlayStatus === 'connecting') {
      handle = window.setTimeout(() => endCall('connection_failed'), CONNECT_TIMEOUT_MS);
    }
    return () => { if (handle) window.clearTimeout(handle); };
  }, [overlayStatus, currentCall?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(() => ({
    startVoiceCall,
    currentCall,
    socketConnected,
    callStatus: overlayStatus,
    isCallMinimized: minimized,
    minimizeCall,
    expandCall,
  }), [currentCall, expandCall, minimizeCall, minimized, overlayStatus, socketConnected, startVoiceCall]);

  const contactName = currentCall?.other_user?.name || 'Allure contact';
  const handleEnd = () => endCall(overlayStatus === 'calling' ? 'no_answer' : 'hangup');

  // Show the WhatsApp-style heads-up banner for ringing (incoming) on every
  // platform — a full-screen takeover in-app is too intrusive when the user
  // is actively using the app. The full overlay still handles calling /
  // connecting / active / reconnecting states.
  const showHeadsUpOnly = overlayStatus === 'incoming';
  const overlayOpen = Boolean(currentCall) && !minimized && !showHeadsUpOnly;

  return (
    <CallContext.Provider value={value}>
      {children}
      <audio ref={remoteAudioRef} autoPlay playsInline style={{ display: 'none' }} />
      <MessengerCallOverlay
        open={overlayOpen}
        onMinimize={minimizeCall}
        canMinimize={overlayStatus !== 'incoming'}
        onEnd={handleEnd}
        onAccept={acceptCall}
        onDecline={declineCall}
        onToggleMute={toggleMute}
        onToggleSpeaker={toggleSpeaker}
        contactName={contactName}
        contactImage={currentCall?.other_user?.image_url}
        dataTestId="global-call-overlay"
        status={overlayStatus}
        durationSeconds={callDurationSeconds}
        isMuted={isMuted}
        speakerOn={speakerOn}
        socketConnected={socketConnected}
      />
      <IncomingCallHeadsUp
        open={showHeadsUpOnly}
        contactName={contactName}
        contactImage={currentCall?.other_user?.image_url}
        onAccept={acceptCall}
        onDecline={declineCall}
      />
      <CallPipBubble
        open={Boolean(currentCall) && minimized && !showHeadsUpOnly}
        name={contactName}
        image={currentCall?.other_user?.image_url}
        status={overlayStatus}
        durationSeconds={callDurationSeconds}
        onExpand={expandCall}
        onEnd={handleEnd}
      />
    </CallContext.Provider>
  );
}

export function useCall() {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used within CallProvider');
  }
  return context;
}
